import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';

interface MarkupRateBadgeProps {
  markupRate?: number;
  jobMarkupRate: number;
  onChange: (markupRate?: number) => void;
  readOnly?: boolean;
}

export function MarkupRateBadge({
  markupRate,
  jobMarkupRate,
  onChange,
  readOnly = false,
}: MarkupRateBadgeProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const hasOverride = markupRate != null;
  const displayRate = hasOverride ? markupRate : jobMarkupRate;
  const displayPercent = (displayRate * 100).toFixed(1);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    setLocalValue(displayPercent);
    setIsEditing(true);
  };

  const handleCommit = () => {
    setIsEditing(false);
    const val = parseFloat(localValue);
    const safeVal = isNaN(val) ? 0 : val;
    const rate = Math.round(safeVal * 10) / 1000; // Convert percent to decimal, e.g. 14.5 -> 0.145
    if (rate < 0 || rate > 1) return;
    // If they set it back to the job rate, clear the override
    if (Math.abs(rate - jobMarkupRate) < 0.0001) {
      onChange(undefined);
    } else {
      onChange(rate);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
        <Input
          ref={inputRef}
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCommit();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          className="h-5 w-14 px-1 text-xs text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="text-[10px] text-muted-foreground">%</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] leading-none
        transition-colors cursor-pointer
        ${hasOverride
          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
        }
      `}
      title={hasOverride ? `Custom markup: ${displayPercent}% (click to edit, right-click to reset)` : `Using job markup: ${displayPercent}% (click to override)`}
      onContextMenu={(e) => {
        if (hasOverride && !readOnly) {
          e.preventDefault();
          handleClear(e);
        }
      }}
    >
      {displayPercent}%
    </button>
  );
}
